import axios from "axios";
import { serializeArrayAndFilterNullable } from "utils/queryUtils";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { type PaginationParams } from "features/Library/Common/models";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type LicenseAccountRow, type LicensesEngagementSummary } from "features/Reporting/types/packs";
import { type LicensesAccountPackRow } from "../Pack/types";

export const LICENSES_ENGAGEMENT_ENDPOINT = "/api/reports/v2/licenses/engagement";
export const LICENSES_ACCOUNTS_ENDPOINT = "/api/reports/v2/licenses/accounts";
export const LICENSES_ACCOUNT_PACKS_ENDPOINT = "/api/reports/v2/licenses/account/packs";
const LICENSES_ACCOUNTS_EXPORT_ENDPOINT = "/api/dataexport/v2/licenses/accounts";
export const LICENSES_ACCOUNT_PACKS_EXPORT_ENDPOINT = "/api/dataexport/v2/licenses/account/packs";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

type LicensesAccountsRequest = PerformanceWithAccountFilter & PaginationParams;

export const getLicensesAccountPacks = (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
  return axios.get<LicensesAccountPackRow[]>(LICENSES_ACCOUNT_PACKS_ENDPOINT, {
    params,
    signal,
    paramsSerializer: serializeArrayAndFilterNullable,
  });
};

export const getLicensesEngagement = (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
  return axios.get<LicensesEngagementSummary>(LICENSES_ENGAGEMENT_ENDPOINT, {
    params,
    signal,
    paramsSerializer: serializeArrayAndFilterNullable,
  });
};

export const getLicensesAccounts = (params: LicensesAccountsRequest, signal?: AbortSignal) => {
  return axios.get<LicenseAccountRow[]>(LICENSES_ACCOUNTS_ENDPOINT, {
    params,
    signal,
    paramsSerializer: serializeArrayAndFilterNullable,
  });
};

export const fetchLicensesAccounts = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<LicenseAccountRow>> => {
  const params: LicensesAccountsRequest = {
    ...filter,
    skip: pagination.skip,
    top: pagination.top,
    sortBy: pagination.sortBy,
    sortOrder: pagination.sortOrder,
  };

  const response = await getLicensesAccounts(params, signal);

  return {
    rows: response.data,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export const fetchLicensesAccountPacks = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<LicensesAccountPackRow>> => {
  const params: PerformanceWithAccountFilter & PaginationParams = {
    ...filter,
    skip: pagination.skip,
    top: pagination.top,
    sortBy: pagination.sortBy,
    sortOrder: pagination.sortOrder,
  };

  const response = await getLicensesAccountPacks(params, signal);

  return {
    rows: response.data,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export const getLicensesAccountsExport = (filter: PerformanceWithAccountFilter, signal?: AbortSignal) => {
  return axios.get(LICENSES_ACCOUNTS_EXPORT_ENDPOINT, {
    responseType: "blob",
    signal,
    params: filter,
    paramsSerializer: serializeArrayAndFilterNullable,
  });
};

export const getLicensesAccountPacksExport = (filter: PerformanceWithAccountFilter, signal?: AbortSignal) => {
  return axios.get(LICENSES_ACCOUNT_PACKS_EXPORT_ENDPOINT, {
    responseType: "blob",
    signal,
    params: filter,
    paramsSerializer: serializeArrayAndFilterNullable,
  });
};
